/**
 * One rule for turning a broker's single "name" input into first/last
 * names, shared by the add-broker modal (what gets stored), the Create
 * Invoice form (what gets shown) and mirroring the backend's own fallback
 * (PRs #405/#406). Keeping a single implementation means the three never
 * disagree on where the split happens.
 */

export interface PersonName {
    first: string;
    last: string;
}

/**
 * The backend's rule: trim, split on the first run of whitespace, first
 * token is the first name and the rest is the last name. A single token is
 * used for both, since Ascend rejects a blank last name; a blank name
 * yields blanks (validation then asks the user).
 */
export function splitBrokerName(name: string | null | undefined): PersonName {
    const trimmed = (name ?? "").trim();
    if (trimmed === "") return { first: "", last: "" };
    const [first, ...rest] = trimmed.split(/\s+/);
    const last = rest.join(" ");
    return { first, last: last === "" ? first : last };
}

export interface BrokerNameFields {
    /** The typed name, trimmed with inner whitespace collapsed. */
    name: string;
    first_name: string;
    /** Absent (not blank, not duplicated) when the input is a single word. */
    last_name?: string;
}

/**
 * Name fields to STORE when creating a broker (OR-1600).
 *
 * Unlike `splitBrokerName`, a single-word name only yields a first name: a
 * duplicated "Cher" / "Cher" would be a guess written into the broker row
 * and pushed to HubSpot. The invoice form derives a last name for Ascend at
 * invoice time from the stored values, so nothing is lost by leaving it out.
 */
export function brokerNameFields(rawName: string): BrokerNameFields {
    const name = rawName.trim().replace(/\s+/g, " ");
    const { first, last } = splitBrokerName(name);
    const hasLast = name.includes(" ");
    return { name, first_name: first, ...(hasLast ? { last_name: last } : {}) };
}
